/**
 * Store Locator - Haus of Babka
 * Uses Leaflet.js with OpenStreetMap for free, accessible mapping
 */

class StoreLocator {
  constructor(config) {
    this.config = config;
    this.stores = config.stores || [];
    this.map = null;
    this.markers = [];
    this.userLocation = null;
    this.activeStoreId = null;
    this.drawer = null;
    this.defaultView = {
      center: [config.defaultCenter.lat, config.defaultCenter.lng],
      zoom: config.defaultZoom
    };
    this.initialBounds = null;
    
    this.init();
  }

  init() {
    // Wait for Leaflet to load
    if (typeof L === 'undefined') {
      console.error('Leaflet library not loaded');
      return;
    }

    this.drawer = document.getElementById('store-details-drawer');
    this.initMap();
    this.initEventListeners();
    this.renderStores();
  }

  initMap() {
    const mapElement = document.getElementById('store-map');
    if (!mapElement) return;

    // Initialize Leaflet map
    this.map = L.map('store-map', {
      scrollWheelZoom: false,
      zoomControl: true
    }).setView(
      [this.config.defaultCenter.lat, this.config.defaultCenter.lng],
      this.config.defaultZoom
    );

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map);

    // Add store markers
    this.addStoreMarkers();

    // Fit bounds to show all stores if there are multiple with valid coordinates
    const validStores = this.stores.filter(store => store.lat != null && store.lng != null);
    if (validStores.length > 1) {
      const bounds = L.latLngBounds(
        validStores.map(store => [store.lat, store.lng])
      );
      this.map.fitBounds(bounds, { padding: [50, 50] });
      this.initialBounds = bounds;
    }

    this.addResetControl();
  }

  addStoreMarkers() {
    this.stores.forEach(store => {
      if (store.lat == null || store.lng == null) return;

      // Create custom icon with brand colors
      const customIcon = L.divIcon({
        className: 'store-marker',
        html: `
          <div class="store-marker__inner" data-store-id="${store.id}">
            <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="var(--color-primary, #000F9F)"/>
              <circle cx="16" cy="16" r="6" fill="white"/>
            </svg>
          </div>
        `,
        iconSize: [32, 40],
        iconAnchor: [16, 40],
        popupAnchor: [0, -40]
      });

      const marker = L.marker([store.lat, store.lng], { icon: customIcon })
        .addTo(this.map);

      // Add click event to open drawer
      marker.on('click', () => {
        this.highlightStore(store.id);
        this.focusMarker(store.id);
        this.openDrawer(store.id);
      });

      this.markers.push({ id: store.id, marker });
    });
  }


  initEventListeners() {
    // Search input filters list
    const searchBtn = document.getElementById('search-location-btn');
    const searchInput = document.getElementById('store-search');

    if (searchBtn && searchInput) {
      const applyFilter = () => this.filterStoreList(searchInput.value);

      searchBtn.addEventListener('click', applyFilter);
      searchInput.addEventListener('input', applyFilter);
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyFilter();
        }
      });
    }

    // Geolocation button
    const geoBtn = document.getElementById('use-my-location-btn');
    if (geoBtn) {
      geoBtn.addEventListener('click', () => this.useGeolocation());
    }

    // Store list item clicks
    const storeItems = document.querySelectorAll('.store-locator__item');
    storeItems.forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't trigger if clicking on a link or button
        if (e.target.closest('a, button')) return;
        
        const storeId = item.dataset.storeId;
        this.highlightStore(storeId);
        this.focusMarker(storeId);
        this.openDrawer(storeId);
      });
    });

    // Drawer close button
    const drawerCloseBtn = document.querySelector('.store-locator__drawer-close');
    if (drawerCloseBtn) {
      drawerCloseBtn.addEventListener('click', () => this.closeDrawer());
    }

    // Drawer close on Escape key
    if (this.drawer) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('open')) {
          this.closeDrawer();
        }
      });
    }

    this.initPanelToggle();
    this.initMobileDropdown();
  }

  filterStoreList(query = '') {
    const normalizedQuery = query.trim().toLowerCase();
    const storeItems = document.querySelectorAll('.store-locator__item');
    if (!storeItems.length) return;

    let visibleCount = 0;

    storeItems.forEach((item) => {
      const name = item.querySelector('.store-locator__item-name')?.textContent.toLowerCase() || '';
      const address = item.querySelector('.store-locator__item-address-content')?.textContent.toLowerCase() || '';
      const matches = !normalizedQuery || name.includes(normalizedQuery) || address.includes(normalizedQuery);

      item.style.display = matches ? '' : 'none';
      if (matches) {
        visibleCount += 1;
      }
    });

    const resultsCount = document.getElementById('results-count');
    if (resultsCount) {
      if (visibleCount === 0) {
        resultsCount.textContent = 'No locations';
      } else if (visibleCount === 1) {
        resultsCount.textContent = '1 location';
      } else {
        resultsCount.textContent = `${visibleCount} locations`;
      }
    }
  }

  useGeolocation() {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    const geoBtn = document.getElementById('use-my-location-btn');
    const originalContent = geoBtn?.innerHTML;

    if (geoBtn) {
      geoBtn.innerHTML = '<span>Finding...</span>';
      geoBtn.disabled = true;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        // Add user location marker
        this.addUserLocationMarker();

        // Calculate and display distances
        this.calculateDistances();

        // Sort stores by distance
        this.sortStoresByDistance();

        // Zoom to user location
        this.map.setView([this.userLocation.lat, this.userLocation.lng], 12);

        // Restore button
        if (geoBtn) {
          geoBtn.innerHTML = originalContent;
          geoBtn.disabled = false;
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please check your browser permissions.');
        
        if (geoBtn) {
          geoBtn.innerHTML = originalContent;
          geoBtn.disabled = false;
        }
      }
    );
  }

  addUserLocationMarker() {
    // Remove existing user marker if any
    if (this.userMarker) {
      this.map.removeLayer(this.userMarker);
    }

    const userIcon = L.divIcon({
      className: 'user-location-marker',
      html: `
        <div class="user-location-marker__inner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#4A90E2" stroke="white" stroke-width="3"/>
          </svg>
        </div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    this.userMarker = L.marker([this.userLocation.lat, this.userLocation.lng], {
      icon: userIcon
    }).addTo(this.map);
  }

  calculateDistances() {
    if (!this.userLocation) return;

    this.stores.forEach(store => {
      if (store.lat == null || store.lng == null) return;

      const distance = this.haversineDistance(
        this.userLocation.lat,
        this.userLocation.lng,
        store.lat,
        store.lng
      );

      store.distance = distance;
    });
  }

  haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  sortStoresByDistance() {
    const storeList = document.getElementById('store-list');
    if (!storeList) return;

    // Sort stores by distance
    const sortedStores = [...this.stores].sort((a, b) => {
      if (a.distance === undefined) return 1;
      if (b.distance === undefined) return -1;
      return a.distance - b.distance;
    });

    // Reorder DOM elements
    sortedStores.forEach(store => {
      const element = storeList.querySelector(`[data-store-id="${store.id}"]`);
      if (element) {
        storeList.appendChild(element);
      }
    });

    // Update results count
    const resultsCount = document.getElementById('results-count');
    if (resultsCount && this.userLocation) {
      resultsCount.textContent = `Sorted by distance`;
    }
  }

  highlightStore(storeId) {
    // Remove previous highlight
    document.querySelectorAll('.store-locator__item').forEach(item => {
      item.classList.remove('store-locator__item--active');
    });

    // Add highlight to selected store
    const storeElement = document.querySelector(`[data-store-id="${storeId}"]`);
    if (storeElement) {
      storeElement.classList.add('store-locator__item--active');
    }

    this.activeStoreId = storeId;
  }

  focusMarker(storeId) {
    const markerObj = this.markers.find(m => m.id === storeId);
    if (!markerObj) return;

    const store = this.stores.find(s => s.id === storeId);
    if (!store) return;

    // Pan to marker
    this.map.setView([store.lat, store.lng], 14, { animate: true });
  }

  openDrawer(storeId) {
    if (!this.drawer) return;

    const store = this.stores.find(s => s.id === storeId);
    if (!store) return;

    // Populate drawer content
    const drawerContent = document.getElementById('store-drawer-content');
    if (!drawerContent) return;

    let html = `
      <div class="store-locator__drawer-store">
        <h3 class="store-locator__drawer-store-name">${this.escapeHtml(store.name)}</h3>
    `;

    if (store.address) {
      html += `
        <div class="store-locator__drawer-address">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M8 0C5.23858 0 3 2.23858 3 5C3 8.5 8 14 8 14C8 14 13 8.5 13 5C13 2.23858 10.7614 0 8 0ZM8 7C6.89543 7 6 6.10457 6 5C6 3.89543 6.89543 3 8 3C9.10457 3 10 3.89543 10 5C10 6.10457 9.10457 7 8 7Z" fill="currentColor"/>
          </svg>
          <div>
            <div>${this.escapeHtml(store.address)}</div>
            ${store.subaddress ? `<div class="store-locator__drawer-subaddress">${this.escapeHtml(store.subaddress)}</div>` : ''}
          </div>
        </div>
      `;
    }

    if (store.phone) {
      html += `
        <div class="store-locator__drawer-phone">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z" fill="currentColor"/>
          </svg>
          <a href="tel:${store.phone.replace(/\s|-/g, '')}">${this.escapeHtml(store.phone)}</a>
        </div>
      `;
    }

    if (store.hours) {
      html += `
        <div class="store-locator__drawer-hours">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14ZM9 8C9 7.44772 8.55228 7 8 7C7.44772 7 7 7.44772 7 8V11C7 11.5523 7.44772 12 8 12C8.55228 12 9 11.5523 9 11V8ZM8 4C7.44772 4 7 4.44772 7 5V5.5C7 6.05228 7.44772 6.5 8 6.5C8.55228 6.5 9 6.05228 9 5.5V5C9 4.44772 8.55228 4 8 4Z" fill="currentColor"/>
          </svg>
          <div class="store-locator__drawer-hours-content rte">${store.hours}</div>
        </div>
      `;
    }

    html += `
        <div class="store-locator__drawer-actions">
    `;

    if (store.lat && store.lng) {
      html += `
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}"
            target="_blank"
            rel="noopener noreferrer"
            class="store-locator__drawer-directions button button--secondary"
          >
            Get Directions
          </a>
      `;
    }

    if (store.website) {
      html += `
          <a
            href="${this.escapeHtml(store.website)}"
            target="_blank"
            rel="noopener noreferrer"
            class="store-locator__drawer-website button button--secondary"
          >
            Visit Website
          </a>
      `;
    }

    if (this.config.enablePreorder) {
      html += `
          <button
            type="button"
            class="store-locator__drawer-preorder button button--primary"
            data-preorder-trigger
            data-stop-name="${this.escapeHtml(store.name)}"
          >
            Preorder
          </button>
      `;
    }

    html += `
        </div>
      </div>
    `;

    drawerContent.innerHTML = html;
    this.drawer.classList.add('open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.activeStoreId = storeId;
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('open');
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  addResetControl() {
    if (!this.map) return;

    const self = this;
    const ResetControl = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const container = L.DomUtil.create('div', 'leaflet-bar store-locator__reset-control');
        const button = L.DomUtil.create('button', 'store-locator__reset-control-button', container);
        button.type = 'button';
        button.setAttribute('aria-label', 'Reset map view');
        button.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
          </svg>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.on(button, 'click', (event) => {
          L.DomEvent.stop(event);
          self.resetMapView();
        });

        return container;
      }
    });

    this.map.addControl(new ResetControl());
  }

  resetMapView() {
    if (!this.map) return;

    if (this.initialBounds) {
      this.map.fitBounds(this.initialBounds, { padding: [50, 50] });
    } else if (this.defaultView) {
      this.map.setView(this.defaultView.center, this.defaultView.zoom);
    }
  }

  initPanelToggle() {
    const panel = document.getElementById('store-panel');
    const toggle = document.getElementById('store-panel-toggle');
    if (!panel || !toggle) return;

    const setState = (open) => {
      if (open) {
        panel.classList.add('store-locator__panel--open');
      } else {
        panel.classList.remove('store-locator__panel--open');
      }
      toggle.setAttribute('aria-expanded', open);
      panel.setAttribute('aria-hidden', (!open).toString());
    };

    setState(panel.classList.contains('store-locator__panel--open'));

    toggle.addEventListener('click', () => {
      const isOpen = panel.classList.contains('store-locator__panel--open');
      setState(!isOpen);
    });
  }

  initMobileDropdown() {
    const trigger = document.getElementById('mobile-dropdown-trigger');
    const panel = document.getElementById('mobile-dropdown-panel');
    const dropdownList = document.getElementById('mobile-dropdown-list');
    const dropdownText = trigger?.querySelector('.store-locator__mobile-dropdown-text');

    if (!trigger || !panel || !dropdownList || !dropdownText) return;

    const dropdownItems = dropdownList.querySelectorAll('.store-locator__mobile-dropdown-item');
    const searchInput = document.getElementById('mobile-store-search');

    const setPanelVisibility = (open) => {
      trigger.setAttribute('aria-expanded', open);
      panel.setAttribute('aria-hidden', (!open).toString());
    };

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      setPanelVisibility(!isExpanded);

      if (!isExpanded && searchInput) {
        setTimeout(() => searchInput.focus(), 80);
      }
    });

    document.addEventListener('click', (event) => {
      if (!panel.contains(event.target) && !trigger.contains(event.target)) {
        setPanelVisibility(false);
      }
    });

    if (searchInput && dropdownItems.length) {
      searchInput.addEventListener('input', (event) => {
        const query = event.target.value.toLowerCase().trim();

        dropdownItems.forEach((item) => {
          const name = item.querySelector('.store-locator__mobile-dropdown-item-name')?.textContent.toLowerCase() || '';
          const address = item.querySelector('.store-locator__mobile-dropdown-item-address')?.textContent.toLowerCase() || '';
          const matches = name.includes(query) || address.includes(query);
          const entry = item.closest('.store-locator__mobile-dropdown-entry') || item;
          entry.style.display = matches ? '' : 'none';
        });
      });
    }

    dropdownItems.forEach((item) => {
      item.addEventListener('click', () => {
        const storeId = item.dataset.storeId;
        if (!storeId) return;

        const name = item.querySelector('.store-locator__mobile-dropdown-item-name')?.textContent || 'Select a location';
        dropdownText.textContent = name;

        setPanelVisibility(false);

        if (searchInput) {
          searchInput.value = '';
          dropdownItems.forEach((dropdownItem) => {
            const entry = dropdownItem.closest('.store-locator__mobile-dropdown-entry') || dropdownItem;
            entry.style.display = '';
          });
        }

        this.highlightStore(storeId);
        this.focusMarker(storeId);
        this.openDrawer(storeId);
      });
    });
  }

  renderStores() {
    // Stores are already rendered in Liquid, this method can be used for dynamic updates
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize store locator when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStoreLocator);
} else {
  initStoreLocator();
}

function initStoreLocator() {
  console.log('Store Locator: Initializing...');
  
  // Check if we're on a page with store locator
  const mapElement = document.getElementById('store-map');
  
  if (!mapElement) {
    console.log('Store Locator: Map element not found');
    return;
  }
  
  if (typeof window.storeLocatorConfig === 'undefined') {
    console.error('Store Locator: Configuration not found');
    return;
  }
  
  console.log('Store Locator: Config found', window.storeLocatorConfig);
  
  // Wait for Leaflet to load from CDN
  if (typeof L === 'undefined') {
    console.log('Store Locator: Waiting for Leaflet to load...');
    // Leaflet not loaded yet, wait a bit
    setTimeout(initStoreLocator, 100);
    return;
  }
  
  console.log('Store Locator: Leaflet loaded, creating map...');
  
  try {
    new StoreLocator(window.storeLocatorConfig);
    console.log('Store Locator: Successfully initialized');
  } catch (error) {
    console.error('Store Locator: Error during initialization', error);
  }
}

